// Phase 31 — Bulk Excel Import Button.
// Drop-in component that handles:
//   1. Vorlage (template) download with pre-filled headers + dropdowns
//   2. File-pick + upload to /api/imports/{entity}
//   3. Result modal with created/updated/errors-per-row report
//
// Usage:
//   <BulkImportButton
//     entity="vehicles"
//     templateUrl="/imports/vehicles/template"
//     uploadUrl="/imports/vehicles"
//     templateFileName="Fahrzeuge_Vorlage.xlsx"
//     label="Fahrzeuge importieren"
//     testid="bulk-import-vehicles"
//     onDone={() => reload()}
//   />

import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, FileSpreadsheet, Download, X, CheckCircle2, AlertCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function BulkImportButton({
  entity,
  templateUrl,
  uploadUrl,
  templateFileName,
  label = "Importieren",
  testid = "bulk-import",
  onDone,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  async function downloadTemplate() {
    setMenuOpen(false);
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = templateFileName || `${entity}_Vorlage.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Vorlage heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorlage konnte nicht erstellt werden");
    }
  }

  function pickFile() {
    setMenuOpen(false);
    fileRef.current?.click();
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Bitte eine .xlsx-Datei wählen");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(uploadUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      setReport({ ...data, fileName: file.name });
      if (onDone) onDone();
      const msg = `${data.created || 0} neu · ${data.updated || 0} aktualisiert`;
      if ((data.errors || []).length === 0) toast.success(`Import OK — ${msg}`);
      else toast.warning(`Import fertig — ${msg} · ${data.errors.length} Hinweise`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="relative inline-block" data-bulk-import-root>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          data-testid={`${testid}-trigger`}
          className="inline-flex items-center gap-2 bg-emerald-600/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/25 text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {busy ? "Lade…" : label}
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 top-full mt-1.5 w-72 bg-zinc-900 border border-zinc-800 rounded-md shadow-2xl z-40"
                 data-testid={`${testid}-menu`}>
              <button
                type="button"
                onClick={downloadTemplate}
                data-testid={`${testid}-template`}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-zinc-800/60 border-b border-zinc-800/50"
              >
                <Download className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-zinc-100">Vorlage herunterladen</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    .xlsx mit allen Spalten und Dropdown-Listen (Status, Abteilung, Rolle …)
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={pickFile}
                data-testid={`${testid}-upload`}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-zinc-800/60"
              >
                <FileSpreadsheet className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-zinc-100">Excel hochladen</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    Datensätze werden anhand eindeutiger Felder erkannt und ggf. aktualisiert.
                  </div>
                </div>
              </button>
            </div>
          </>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={onFile}
        data-testid={`${testid}-file-input`}
      />
      {report && (
        <ImportReportModal
          report={report}
          onClose={() => setReport(null)}
          testid={`${testid}-report`}
        />
      )}
    </>
  );
}

function ImportReportModal({ report, onClose, testid }) {
  const errors = report.errors || [];
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60]" data-testid={testid}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl max-h-[88vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-zinc-50">Import-Ergebnis</h3>
            <p className="text-xs text-zinc-500 mt-0.5 font-mono">{report.fileName}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid={`${testid}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Neu angelegt" value={report.created || 0} tone="emerald" icon={CheckCircle2} />
            <Stat label="Aktualisiert" value={report.updated || 0} tone="blue" icon={CheckCircle2} />
            <Stat label="Hinweise" value={errors.length} tone={errors.length > 0 ? "rose" : "zinc"} icon={AlertCircle} />
          </div>

          {errors.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                Hinweise pro Zeile
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-zinc-950 border-b border-zinc-800">
                    <tr className="text-zinc-500">
                      <th className="text-left px-2 py-1.5 font-medium">Zeile</th>
                      <th className="text-left px-2 py-1.5 font-medium">Feld</th>
                      <th className="text-left px-2 py-1.5 font-medium">Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={i} className="border-b border-zinc-800/40 last:border-b-0">
                        <td className="px-2 py-1.5 font-mono text-zinc-300">{e.row}</td>
                        <td className="px-2 py-1.5 text-amber-300">{e.field}</td>
                        <td className="px-2 py-1.5 text-zinc-300">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-[11px] text-zinc-500">
            Tipp: Bestehende Datensätze werden anhand der Schlüssel-Felder (z.B. Kennzeichen, E-Mail, Karten-Nummer) erkannt und überschrieben — fülle nur die Spalten, die du ändern willst.
          </p>
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium" data-testid={`${testid}-ok`}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, icon: Icon }) {
  const map = {
    emerald: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
    blue: "bg-blue-500/10 border-blue-500/40 text-blue-300",
    rose: "bg-rose-500/10 border-rose-500/40 text-rose-300",
    zinc: "bg-zinc-800/40 border-zinc-700 text-zinc-300",
  };
  return (
    <div className={`rounded-md border p-3 ${map[tone]}`}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
}
